/**
 * The horizontal geometry of a page's content column: how wide it may grow and
 * the gutter it keeps from the viewport edge. Everything on a template that
 * must share a left edge (breadcrumb, hero copy, section headings) takes its
 * width from this map, so the edge is spelled once instead of per component.
 *
 * `page` is the landing geometry: 1280px at most, centred, with a 16px gutter,
 * 48px from `lg` and 80px from `xl`. Content edges therefore land at
 * 16/16/48/80/160/400px at 375/768/1024/1280/1440/1920px viewports.
 *
 * `w-full`, not Tailwind's `container`: `container` is not inert. It supplies
 * `width: 100%` (without which a flex child shrinks to its content) plus
 * per-breakpoint max-widths that `max-w-7xl` happens to override; `w-full`
 * says the one thing that is needed.
 *
 * Read it through `PageContainer`, whose `className` is for vertical spacing,
 * background, flex or grid only: a caller must not add horizontal padding,
 * margin or width, which would move the shared edge. That is a convention,
 * not enforced. `LANDING_CONTAINER_CLASS` spells the same box with
 * `container`; it is left alone for now because page-builder blocks read it
 * too, and a test pins the two to the same geometry.
 */
export const PAGE_WIDTH_CLASS = {
  /**
   * The long-form reading column (policies, blog posts): a 40rem (640px) text
   * measure, centred, with a flat 16px gutter. The gutter sits INSIDE the
   * 42rem box, as `page`'s does inside its 80rem one, so the content is exactly
   * 640px from a 672px viewport up. Edges 16/64/192/320/400/640. Named
   * `reading`, not `prose`, so it cannot be mistaken for the typography plugin.
   */
  reading: 'mx-auto w-full max-w-2xl px-4',
  page: 'mx-auto w-full max-w-7xl px-4 lg:px-12 xl:px-20',
} as const satisfies Record<string, string>;

export type PageWidth = keyof typeof PAGE_WIDTH_CLASS;

/**
 * `sizes` for an image that fills the {@link PAGE_WIDTH_CLASS.reading} column:
 * the viewport less its two 16px gutters until the 42rem (672px) box stops
 * growing, then the 640px content. Change it with the `reading` key.
 */
export const READING_IMAGE_SIZES = '(max-width: 671px) calc(100vw - 2rem), 640px';
